import React, { startTransition, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { AppSchemasAuthUserUpdate, HTTPValidationError } from 'src/api/Api'; // Assuming AppSchemasAuthUserUpdate is the correct type

const ChangePasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [passwords, setPasswords] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({
        newPassword: '',
        confirmPassword: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswords((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear errors on change
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors = { newPassword: '', confirmPassword: '' };

        if (!passwords.newPassword.trim()) {
            newErrors.newPassword = 'New Password is required.';
            isValid = false;
        } else if (passwords.newPassword.length < 8) { // Example: Minimum length validation
            newErrors.newPassword = 'Password must be at least 8 characters long.';
            isValid = false;
        }

        if (!passwords.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm Password is required.';
            isValid = false;
        } else if (passwords.newPassword !== passwords.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        const payload: AppSchemasAuthUserUpdate = {
            password: passwords.newPassword,
        };

        clientService.api
            .updateUserApiV1AuthUserPut(payload)
            .then(() => {
                showSnackbar({ message: 'Password changed successfully!', type: snackbarType.SUCCESS });
                setPasswords({ newPassword: '', confirmPassword: '' }); // Clear form
                // Optionally navigate away or to login page if session needs refresh
                // navigate('/profile'); // Or wherever appropriate
            })
            .catch((error: HTTPValidationError | any) => {
                const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Error changing password.';
                showSnackbar({
                    message: `Error: ${errorMessage}`,
                    type: snackbarType.ERROR,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={() => startTransition(() => navigate(-1))} color="primary" sx={{ mr: 1 }} aria-label="Back" disabled={isSubmitting}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1">Change Password</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="New Password"
                            name="newPassword"
                            value={passwords.newPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="Confirm Password"
                            name="confirmPassword"
                            value={passwords.confirmPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button onClick={() => startTransition(() => navigate(-1))} color="inherit" disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !passwords.newPassword || !passwords.confirmPassword} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default ChangePasswordPage;